import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../api/client'
import { useApiHealth } from '../api/hooks'
import type { AuthMethod, CustomerGrant, MfaMethod } from '../api/types'
import { hasValidCustomerToken, hasValidIdentityToken, useAuthStore } from '../stores/auth'
import { initials } from '../lib/format'
import { storePendingOAuth } from '../lib/oauth'
import { passkeyErrorMessage, runPasskeyAuthentication, webauthnSupported } from '../lib/webauthn'
import { Button, Chip, Input, Spinner } from '../components/ui'
import { UpdateBanner } from '../components/UpdateBanner'
import { OstrelMark } from '../components/OstrelMark'

const DEFAULT_METHODS: AuthMethod[] = [
  { id: 'password', kind: 'password', name: 'Email and password', enabled: true },
  { id: 'service', kind: 'service', name: 'Service account', enabled: true },
]

const CONNECTORS = ['Axema Vaka', 'Techfass', 'Acre / SPC', 'Came BPT']

function ssoTag(method: AuthMethod): string {
  return method.kind === 'saml' ? 'SAML' : method.id.toLowerCase().includes('bankid') ? 'SE' : 'SSO'
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  const login = useAuthStore((s) => s.login)
  const loginService = useAuthStore((s) => s.loginService)
  const loginWithIdentityToken = useAuthStore((s) => s.loginWithIdentityToken)
  const selectCustomer = useAuthStore((s) => s.selectCustomer)
  const identityToken = useAuthStore((s) => s.identityToken)
  const principal = useAuthStore((s) => s.principal)

  const [step, setStep] = useState<1 | 2>(() => (hasValidIdentityToken() ? 2 : 1))
  const [methods, setMethods] = useState<AuthMethod[]>(DEFAULT_METHODS)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** Provider id whose authorize URL is being fetched (button spinner). */
  const [oauthBusy, setOauthBusy] = useState<string | null>(null)
  /** Passkey ceremony in flight (primary login or MFA bound ceremony). */
  const [passkeyBusy, setPasskeyBusy] = useState(false)

  // Step 1 fields
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showService, setShowService] = useState(false)
  const [serviceAccount, setServiceAccount] = useState('')
  const [secret, setSecret] = useState('')

  // MFA challenge (password accepted, second factor required). The challenge
  // is single-use: a failed TOTP/recovery attempt consumes it and the whole
  // login restarts at the password.
  const [mfa, setMfa] = useState<{ challenge_id: string; methods: MfaMethod[] } | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaUseRecovery, setMfaUseRecovery] = useState(false)

  // Step 2 data
  const [customers, setCustomers] = useState<CustomerGrant[] | null>(null)
  const [customersError, setCustomersError] = useState<string | null>(null)

  useEffect(() => {
    api
      .authMethods()
      .then((res) => setMethods(res.methods))
      .catch(() => setMethods(DEFAULT_METHODS)) // endpoint pending — fall back to password
  }, [])

  useEffect(() => {
    if (step !== 2 || !identityToken) return
    setCustomersError(null)
    api
      .customers()
      .then(setCustomers)
      .catch((err: unknown) => {
        setCustomers([])
        setCustomersError(
          err instanceof ApiError && err.status === 404
            ? 'Customer listing is not available on this API yet.'
            : err instanceof Error
              ? err.message
              : 'Failed to load customers.',
        )
      })
  }, [step, identityToken])

  if (hasValidCustomerToken()) return <Navigate to={from} replace />

  async function submitPassword(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const challenge = await login(username.trim(), password)
      if (challenge) {
        // MFA gate — second factor required before step 2.
        setMfa({ challenge_id: challenge.mfa_challenge, methods: challenge.mfa_methods as MfaMethod[] })
        setMfaCode('')
        setMfaUseRecovery(!challenge.mfa_methods.includes('totp'))
        setPassword('')
      } else {
        setStep(2)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Sign-in failed.')
    } finally {
      setBusy(false)
    }
  }

  /** Passwordless primary login: options → authenticator → verify, then the
   *  same identity-token flow as a password login. */
  async function signInWithPasskey() {
    setError(null)
    setPasskeyBusy(true)
    try {
      const email = username.trim()
      const { challenge_id, options } = await api.passkeyLoginOptions(email ? { email } : {})
      const credential = await runPasskeyAuthentication(options)
      const { identity_token } = await api.passkeyLoginVerify({ challenge_id, credential })
      if (!identity_token) throw new Error('Passkey verification failed.')
      await loginWithIdentityToken(identity_token)
      setStep(2)
    } catch (err) {
      setError(passkeyErrorMessage(err))
    } finally {
      setPasskeyBusy(false)
    }
  }

  /** MFA second factor via TOTP or recovery code. A wrong code CONSUMES the
   *  challenge server-side, so any failure restarts the login at the
   *  password step. */
  async function submitMfaCode(e: FormEvent) {
    e.preventDefault()
    if (!mfa) return
    setError(null)
    setBusy(true)
    try {
      const { identity_token } = await api.mfaVerify(
        mfaUseRecovery
          ? { challenge_id: mfa.challenge_id, recovery_code: mfaCode.trim() }
          : { challenge_id: mfa.challenge_id, totp_code: mfaCode.trim() },
      )
      await loginWithIdentityToken(identity_token)
      setMfa(null)
      setStep(2)
    } catch {
      setMfa(null)
      setMfaCode('')
      setError('Verification failed — sign in again.')
    } finally {
      setBusy(false)
    }
  }

  /** MFA second factor via a passkey ceremony bound to the challenge. A
   *  failed ceremony leaves the challenge alive (verify is the consuming
   *  step), so the operator can retry. */
  async function mfaWithPasskey() {
    if (!mfa) return
    setError(null)
    setPasskeyBusy(true)
    try {
      const { challenge_id, options } = await api.passkeyLoginOptions({ mfa_challenge: mfa.challenge_id })
      const credential = await runPasskeyAuthentication(options)
      const { identity_token } = await api.passkeyLoginVerify({ challenge_id, credential })
      if (!identity_token) throw new Error('Passkey verification failed.')
      await loginWithIdentityToken(identity_token)
      setMfa(null)
      setStep(2)
    } catch (err) {
      setError(passkeyErrorMessage(err))
    } finally {
      setPasskeyBusy(false)
    }
  }

  async function submitService(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await loginService(serviceAccount.trim(), secret)
      setStep(2)
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Sign-in failed.')
    } finally {
      setBusy(false)
    }
  }

  async function startOAuth(m: AuthMethod) {
    setError(null)
    setOauthBusy(m.id)
    try {
      const { authorize_url } = await api.oauthAuthorize(m.id)
      // Remember the provider so /auth/callback knows which endpoint to POST to.
      storePendingOAuth({ id: m.id, name: m.name })
      window.location.assign(authorize_url)
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Could not start single sign-on.')
      setOauthBusy(null)
    }
  }

  async function chooseCustomer(uid: string) {
    setError(null)
    setBusy(true)
    try {
      await selectCustomer(uid)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Could not scope to customer.')
      setBusy(false)
    }
  }

  const oauthMethods = methods.filter((m) => m.kind === 'oauth' || m.kind === 'saml')
  const passwordEnabled = methods.some((m) => m.kind === 'password' && m.enabled)
  const serviceEnabled = methods.some((m) => m.kind === 'service' && m.enabled)

  return (
    <div className="flex min-h-screen items-center justify-center bg-ostrel-navy p-6">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-sm border border-ostrel-border md:grid-cols-[2fr_3fr]">
        {/* Brand panel */}
        <div className="flex flex-col bg-ostrel-navy p-8 max-md:hidden">
          <div className="flex items-center gap-2">
            <OstrelMark size={36} />
            <span className="flex flex-col">
              <span className="font-display text-2xl leading-none text-ostrel-light">ostrel</span>
              <span className="mt-1 text-[9px] font-semibold uppercase tracking-widest text-ostrel-dim">
                security
              </span>
            </span>
          </div>
          <p className="mt-4 text-[9px] font-semibold uppercase tracking-widest text-ostrel-dim">
            One interface, every system
          </p>
          <ul className="mt-8 ml-1 border-l border-ostrel-gold/40">
            {CONNECTORS.map((name) => (
              <li key={name} className="flex items-center gap-2.5 py-1.5 pl-4">
                <span className="-ml-[21px] h-1.5 w-1.5 border border-ostrel-gold/60 bg-ostrel-navy" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-muted">
                  {name}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-auto pt-8 text-[10px] leading-relaxed text-ostrel-dim">
            <p>Operator console · ostrel.app</p>
            <LoginVersions />
            <p>© 2026 Clearminds AB</p>
          </div>
        </div>

        {/* Form panel */}
        <div className="bg-ostrel-surface p-8 md:p-10">
          {step === 1 ? (
            mfa ? (
              <>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">
                  Step 1 of 2 · Second factor
                </p>
                <h1 className="mt-2 font-display text-3xl text-ostrel-light">Verify it&apos;s you</h1>
                <p className="mt-1 text-sm text-ostrel-muted">
                  Your password was accepted — complete sign-in with a second factor.
                </p>

                {(mfaUseRecovery && mfa.methods.includes('recovery')) ||
                (!mfaUseRecovery && mfa.methods.includes('totp')) ? (
                  <form onSubmit={submitMfaCode} className="mt-6 flex flex-col gap-4">
                    {mfaUseRecovery ? (
                      <Input
                        tone="dark"
                        label="Recovery code"
                        type="text"
                        autoComplete="off"
                        required
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                      />
                    ) : (
                      <Input
                        tone="dark"
                        label="Authenticator code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                      />
                    )}
                    <Button type="submit" className="w-full py-2.5" disabled={busy || passkeyBusy}>
                      {busy ? 'Verifying…' : 'Verify and continue'}
                    </Button>
                    {mfa.methods.includes('recovery') && mfa.methods.includes('totp') && (
                      <button
                        type="button"
                        onClick={() => {
                          setMfaUseRecovery((v) => !v)
                          setMfaCode('')
                          setError(null)
                        }}
                        className="text-xs text-ostrel-muted transition-colors hover:text-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                      >
                        {mfaUseRecovery ? '← Use an authenticator code' : 'Use a recovery code'}
                      </button>
                    )}
                  </form>
                ) : null}

                {mfa.methods.includes('passkey') && webauthnSupported && (
                  <button
                    type="button"
                    disabled={busy || passkeyBusy}
                    onClick={mfaWithPasskey}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm bg-ostrel-light px-4 py-2.5 text-sm font-medium text-ostrel-ink transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-ostrel-gold disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {passkeyBusy && <Spinner className="h-4 w-4 border-ostrel-ink/30 border-t-ostrel-ink" />}
                    Use a passkey instead
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setMfa(null)
                    setMfaCode('')
                    setError(null)
                  }}
                  className="mt-4 text-xs text-ostrel-dim transition-colors hover:text-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                >
                  ← Start over
                </button>
              </>
            ) : (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">
                Step 1 of 2 · Identity
              </p>
              <h1 className="mt-2 font-display text-3xl text-ostrel-light">Sign in</h1>
              <p className="mt-1 text-sm text-ostrel-muted">
                Operator access to connected access-control systems.
              </p>

              {/* Passwordless-first: the passkey login is the primary path.
                  The Email field above the password form doubles as an
                  optional hint for the ceremony. */}
              {webauthnSupported && (
                <div className="mt-6">
                  <button
                    type="button"
                    disabled={busy || passkeyBusy || oauthBusy !== null}
                    onClick={signInWithPasskey}
                    className="flex w-full items-center justify-center gap-2 rounded-sm bg-ostrel-light px-4 py-2.5 text-sm font-medium text-ostrel-ink transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-ostrel-gold disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {passkeyBusy && <Spinner className="h-4 w-4 border-ostrel-ink/30 border-t-ostrel-ink" />}
                    Sign in with passkey
                  </button>
                  <p className="mt-1.5 text-center text-[9px] uppercase tracking-widest text-ostrel-dim">
                    No password needed — uses this device&apos;s passkey
                  </p>
                </div>
              )}

              {oauthMethods.length > 0 && (
                <div className="mt-6 flex flex-col gap-2">
                  {oauthMethods.map((m) => (
                    <div key={m.id}>
                      <button
                        type="button"
                        disabled={!m.enabled || m.kind !== 'oauth' || oauthBusy !== null}
                        onClick={() => startOAuth(m)}
                        className="flex w-full items-center justify-between rounded-sm bg-ostrel-light px-4 py-2.5 text-sm font-medium text-ostrel-ink transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-ostrel-gold disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        <span className="flex items-center gap-2">
                          {oauthBusy === m.id && (
                            <Spinner className="h-4 w-4 border-ostrel-ink/30 border-t-ostrel-ink" />
                          )}
                          Continue with {m.name}
                        </span>
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                          {ssoTag(m)}
                        </span>
                      </button>
                      {!m.enabled && (
                        <p className="mt-1 text-center text-[9px] uppercase tracking-widest text-ostrel-dim">
                          Not configured
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {passwordEnabled && (
                <>
                  {(webauthnSupported || oauthMethods.length > 0) && (
                    <div className="mt-6 flex items-center gap-3">
                      <span className="h-px flex-1 bg-ostrel-border" />
                      <span className="text-[9px] font-semibold uppercase tracking-widest text-ostrel-dim">
                        Or email and password
                      </span>
                      <span className="h-px flex-1 bg-ostrel-border" />
                    </div>
                  )}
                  <form onSubmit={submitPassword} className="mt-6 flex flex-col gap-4">
                    <Input
                      tone="dark"
                      label="Email"
                      type="text"
                      autoComplete="username"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                    <Input
                      tone="dark"
                      label="Password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button type="submit" className="w-full py-2.5" disabled={busy}>
                      {busy ? 'Signing in…' : 'Continue'}
                    </Button>
                  </form>
                </>
              )}

              {serviceEnabled && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowService((v) => !v)}
                    className="text-xs text-ostrel-muted transition-colors hover:text-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                  >
                    {showService ? '− Hide service account sign-in' : '+ Sign in with a service account'}
                  </button>
                  {showService && (
                    <form
                      onSubmit={submitService}
                      className="mt-3 flex flex-col gap-4 border-t border-ostrel-border pt-4"
                    >
                      <p className="rounded-sm border border-ostrel-warn/40 bg-ostrel-warn/10 px-3 py-2 text-[10px] leading-relaxed text-ostrel-warn">
                        Service accounts are meant for API/bridge use. Interactive login is allowed,
                        but use a regular operator account for day-to-day console work.
                      </p>
                      <Input
                        tone="dark"
                        label="Service account"
                        type="text"
                        placeholder="osa-1"
                        required
                        value={serviceAccount}
                        onChange={(e) => setServiceAccount(e.target.value)}
                      />
                      <Input
                        tone="dark"
                        label="Secret"
                        type="password"
                        required
                        value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                      />
                      <Button type="submit" variant="navy" className="w-full border border-ostrel-border" disabled={busy}>
                        {busy ? 'Signing in…' : 'Continue'}
                      </Button>
                    </form>
                  )}
                </div>
              )}

              <p className="mt-6 text-[10px] text-ostrel-dim">
                Two-step JWT · a scoped token is issued per customer after step 2.
              </p>
            </>
            )
          ) : (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">
                Step 2 of 2 · Customer scope
              </p>
              <h1 className="mt-2 font-display text-3xl text-ostrel-light">Choose customer</h1>
              <p className="mt-1 text-sm text-ostrel-muted">This session is scoped to one customer.</p>

              {principal?.kind === 'service_account' && (
                <div className="mt-4 rounded-sm border-l-4 border-ostrel-warn bg-ostrel-warn/10 p-4">
                  <p className="text-sm font-semibold uppercase tracking-wide text-ostrel-warn">
                    THIS IS A SERVICE ACCOUNT — service accounts should not be used for normal console
                    access.
                  </p>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-2">
                {customers === null && !customersError && (
                  <div className="flex justify-center py-6">
                    <Spinner />
                  </div>
                )}
                {customersError && (
                  <p className="rounded-sm border border-ostrel-warn/40 bg-ostrel-warn/10 px-3 py-2 text-xs text-ostrel-warn">
                    {customersError}
                  </p>
                )}
                {customers?.length === 0 && !customersError && (
                  <p className="text-sm text-ostrel-muted">No customer grants on this identity.</p>
                )}
                {customers?.map((c) => (
                  <button
                    key={c.unique_id}
                    onClick={() => chooseCustomer(c.unique_id)}
                    disabled={busy}
                    className="flex items-center gap-3 rounded-sm border border-ostrel-border bg-ostrel-navy px-3 py-3 text-left transition-colors hover:border-ostrel-gold focus:outline-none focus:ring-2 focus:ring-ostrel-gold disabled:opacity-60"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-ostrel-gold/40 bg-ostrel-gold/10 font-display text-sm text-ostrel-gold">
                      {initials(c.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ostrel-light">{c.name}</span>
                      <span className="tnum block truncate text-xs text-ostrel-muted">
                        {c.slug} · {c.unique_id}
                      </span>
                    </span>
                    {c.role && <Chip tone="gold" label={c.role} />}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => {
                  useAuthStore.getState().logout()
                  setStep(1)
                  setCustomers(null)
                }}
                className="mt-4 text-xs text-ostrel-dim transition-colors hover:text-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
              >
                ← Use a different identity
              </button>
            </>
          )}

          {error && (
            <p className="mt-4 rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
              {error}
            </p>
          )}
        </div>
      </div>
      {/* Pre-auth screens share no layout with AppShell — the new-deploy
          banner mounts here too so a stale bundle never logs a session in. */}
      <UpdateBanner />
    </div>
  )
}

/** Running versions under the console tagline: this bundle's build
 *  (`__APP_VERSION__`) and the API's release from the public /healthz. */
function LoginVersions() {
  const health = useApiHealth()
  return (
    <p className="font-mono" data-testid="login-versions">
      Console {__APP_VERSION__} · API {health.data?.version ?? (health.isError ? '—' : '…')}
    </p>
  )
}
